<template>
<section>
<b-alert v-if="error" ref="errorAlert" tabindex="-1" show variant="danger" role="alert">{{ error }}</b-alert>

          <b-row>
            <b-col lg="4">
              <b-form @submit.prevent="submitUpload">
                <b-form-group label="Emerald export (CSV)" label-for="registry-upload-file" :description="uploadLimitDescription">
                  <b-form-file id="registry-upload-file" :disabled="!canUpdate || busy" v-model="uploadForm.file" accept=".csv,text/csv" :state="uploadFileError ? false : null"/>
                  <b-form-invalid-feedback :state="uploadFileError ? false : null">{{ uploadFileError }}</b-form-invalid-feedback>
                </b-form-group>
                <b-link v-b-toggle.registry-import-details class="d-inline-block mb-2">Additional details (optional)</b-link>
                <b-collapse id="registry-import-details">
                  <b-form-group label="Dataset kind" label-for="registry-upload-dataset_kind" description="Detected from headers. Choose a kind to check it matches.">
                    <b-form-select id="registry-upload-dataset_kind" :disabled="!canUpdate || busy" v-model="uploadForm.dataset_kind" :options="datasetKindOptions"/>
                  </b-form-group>
                  <b-form-group label="Source export date/time" label-for="registry-upload-declared_export_at"
                                description="Optional. Warns if an applied export is newer. Apply order determines precedence.">
                    <b-input-group>
                      <b-form-input id="registry-upload-declared_export_at" :disabled="!canUpdate || busy" v-model="uploadForm.declared_export_at" type="datetime-local" step="1"/>
                      <b-form-select aria-label="Source export timezone" :disabled="!canUpdate || busy" v-model="uploadForm.declared_export_timezone" :options="timezoneOptions" class="registry-timezone"/>
                    </b-input-group>
                  </b-form-group>
                  <b-form-group label="Scope notes" label-for="registry-upload-scope_notes" description="Plain text, e.g. “Colorado, all statuses, partition 2 of 5”.">
                    <b-form-textarea id="registry-upload-scope_notes" :disabled="!canUpdate || busy" v-model.trim="uploadForm.scope_notes" rows="2" maxlength="2000"/>
                  </b-form-group>
                </b-collapse>
                <div>
                  <b-btn type="submit" variant="primary" :disabled="busy || !uploadForm.file || !canUpdate || !!uploadFileError">
                    Validate for review
                    <b-spinner v-if="uploading" small class="ml-1"/>
                  </b-btn>
                </div>
                <small class="text-muted d-block mt-2">Validate first, then review and apply. Validation does not change registry records.</small>
              </b-form>
            </b-col>

            <b-col lg="8">
              <b-card v-if="review" class="registry-import-review">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h5 class="mb-0">Batch #{{ review.batch_id }} · {{ review.original_file_name }}</h5>
                    <small class="text-muted">{{ labelize(review.dataset_kind) }} export · uploaded by {{ review.initiated_by_email || review.initiated_by_user_id || "System" }}</small>
                  </div>
                  <div class="text-right">
                    <b-badge :variant="phaseVariant(review.phase)">{{ phaseLabel(review.phase) }}</b-badge>
                    <b-btn size="sm" variant="link" class="p-0 ml-2" :disabled="busy" @click="closeReview">Close</b-btn>
                  </div>
                </div>

                <b-alert v-if="reviewNotice" show :variant="reviewNotice.variant" class="py-2">{{ reviewNotice.text }}</b-alert>
                <p class="small mb-2">{{ readable(phaseDescription(review)) }}</p><b-alert v-if="reviewError" show variant="danger">{{ reviewError }} <b-btn size="sm" variant="link" @click="loadReview(review.batch_id)">Retry</b-btn></b-alert>

                <dl class="row small mb-2">
                  <dt class="col-sm-4">Kind</dt>
                  <dd class="col-sm-8">{{ labelize(review.dataset_kind) }}</dd>
                  <dt class="col-sm-4">Rows in file</dt>
                  <dd class="col-sm-8">{{ review.row_count === null ? "?" : review.row_count }}</dd>
                  <dt class="col-sm-4">Skipped at validation</dt>
                  <dd class="col-sm-8">{{ review.counts.skipped }}</dd>
                  <dt class="col-sm-4">States in file</dt>
                  <dd class="col-sm-8">{{ observedStates(review) }}</dd>
                  <dt class="col-sm-4">Source export time</dt>
                  <dd class="col-sm-8">{{ review.declared_export_at_local || "Not supplied" }}</dd>
                  <dt class="col-sm-4">Scope notes</dt>
                  <dd class="col-sm-8 registry-notes">{{ readable(scopeNotes(review)) || "None" }}</dd>
                  <template v-if="review.accepted_sequence">
                    <dt class="col-sm-4">Applied</dt>
                    <dd class="col-sm-8">
                      sequence {{ review.accepted_sequence }} · {{ formatDate(review.accepted_at) }}
                      <span v-if="review.accepted_by_email"> by {{ review.accepted_by_email }}</span>
                    </dd>
                  </template>
                  <template v-if="review.accepted_sequence">
                    <dt class="col-sm-4">Results</dt>
                    <dd class="col-sm-8">{{ countsSummary(review) }}</dd>
                  </template>
                </dl>

                <div v-if="review.warnings.length" class="mb-2">
                  <div class="small font-weight-bold">Warnings</div>
                  <div v-for="warning in review.warnings" :key="warning.code" class="small">
                    <b-badge variant="warning">{{ warning.count }}</b-badge> {{ labelize(warning.code) }}
                    <span v-if="warning.labels" class="text-muted">({{ warning.labels.map((l) => `${l.label} × ${l.count}`).join(", ") }})</span>
                  </div>
                </div>

                <div v-if="review.counts.skipped" class="mb-2">
                  <div class="small font-weight-bold">Skipped reasons</div>
                  <div v-for="reason in review.skip_reasons || []" :key="reason.code" class="small">
                    <b-badge variant="danger">{{ reason.count }}</b-badge> {{ labelize(reason.code) }}
                  </div>
                  <b-btn size="sm" variant="link" class="p-0" @click="openRows(review)">View skipped rows</b-btn>
                </div>

                <b-alert v-if="review.older_export" show variant="warning" class="py-2">
                  Batch #{{ review.older_export.newer_batch.id }} has a newer export date, {{ formatUtc(review.older_export.newer_batch.declared_export_at) }}.
                  Applying this older file gives it precedence.
                  <b-form-checkbox v-model="applyAcknowledged" :disabled="!canUpdate || busy" class="mt-2">
                    I understand this file is an older export and still want to apply it.
                  </b-form-checkbox>
                </b-alert>

                <details class="mb-3"><summary>Source and audit details</summary><pre class="registry-import-evidence">{{ formatJson(review) }}</pre></details>
                <div class="d-flex flex-wrap align-items-center">
                  <b-btn v-if="review.available_actions.includes('apply')" variant="success" class="mr-2"
                         :disabled="!canUpdate || busy || (review.older_export && !applyAcknowledged)" @click="applyImport(review)">
                    Apply
                    <b-spinner v-if="applying" small class="ml-1"/>
                  </b-btn>
                  <b-btn v-if="review.available_actions.includes('retry')" variant="outline-warning" class="mr-2"
                         :disabled="!canUpdate || busy" @click="retryImport(review, false)">{{ retryLabel(review) }}</b-btn>
                  <b-btn v-if="review.available_actions.includes('revalidate')" variant="outline-warning" class="mr-2"
                         :disabled="!canUpdate || busy" @click="retryImport(review, true)">Revalidate</b-btn>
                  <b-btn v-if="review.status !== 'rejected'" variant="outline-secondary" size="sm" @click="openRows(review)">Rows</b-btn>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <hr>

          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">Import batches</h5>
            <div>
              <small v-if="pollStopped" class="text-danger mr-2">Auto-refresh stopped after an error.</small>
              <small v-else-if="pollTimer" class="text-muted mr-2">Refreshing while work is active</small>
              <b-btn size="sm" variant="outline-secondary" :disabled="importsLoading" @click="loadImports(importsMeta.current_page || 1)">
                Refresh
              </b-btn>
            </div>
          </div>
          <b-alert v-if="importsError" show variant="danger">{{ importsError }} <b-btn size="sm" variant="link" @click="loadImports(importsPage)">Retry</b-btn></b-alert>
          <b-table v-else :items="imports" :fields="importFields" :busy="importsLoading && !imports.length" small responsive show-empty empty-text="No imports found.">
            <template #table-busy><p role="status">Loading imports...</p></template>
            <template #cell(file)="row"><strong>{{ row.item.original_file_name }}</strong><div class="small text-muted">{{ labelize(row.item.dataset_kind) }} · Batch {{ row.item.batch_id }}</div></template>
            <template #cell(status)="row">
              <b-badge :variant="phaseVariant(row.item.phase)">{{ phaseLabel(row.item.phase) }}</b-badge>
              <div v-if="row.item.rejection" class="small text-danger">{{ readable(row.item.rejection.message) }}</div>
              <div v-else class="small text-muted">{{ readable(phaseDescription(row.item)) }}</div>
            </template>
            <template #cell(progress)="row">
              {{ row.item.processed_row_count }} / {{ row.item.row_count === null ? '?' : row.item.row_count }}
            </template>
            <template #cell(counts)="row">
              <small>{{ countsSummary(row.item) }}</small>
            </template>
            <template #cell(warnings)="row">
              <span v-b-tooltip.hover :title="row.item.warnings.map((w) => `${w.code} × ${w.count}`).join('\n')">
                {{ row.item.counts.warnings }}
              </span>
            </template>
            <template #cell(declared_export_at)="row">
              {{ row.item.declared_export_at_local || 'Not supplied' }}
            </template>
            <template #cell(actor)="row">
              {{ row.item.initiated_by_email || row.item.initiated_by_user_id || 'System' }}
            </template>
            <template #cell(actions)="row">
              <b-btn size="sm" variant="outline-primary" class="mr-1" @click="openReview(row.item)">
                {{ row.item.available_actions.includes('apply') ? 'Review' : 'Details' }}
              </b-btn>
              <b-btn size="sm" variant="outline-secondary" class="mr-1" :disabled="row.item.status === 'rejected'" @click="openRows(row.item)">Rows</b-btn>
              <b-btn v-if="row.item.available_actions.includes('retry')" size="sm" variant="outline-warning"
                     class="mr-1" :disabled="!canUpdate || busy" @click="retryImport(row.item, false)">{{ retryLabel(row.item) }}</b-btn>
              <b-btn v-if="row.item.available_actions.includes('revalidate')" size="sm" variant="outline-warning" :disabled="!canUpdate || busy"
                     @click="retryImport(row.item, true)">Revalidate</b-btn>
            </template>
          </b-table>
          <b-pagination v-if="importsMeta.total" :key="importsRequest" :value="importsPage" :total-rows="importsMeta.total"
                        :per-page="importsMeta.per_page || 25" align="center" @input="loadImports"/>
    <b-modal v-model="rowsModalVisible" :title="rowsBatch ? `Rows of batch #${rowsBatch.batch_id}` : 'Rows'" size="xl" hide-footer>
      <b-row>
        <b-col md="6">
          <b-form-group label="Results">
            <b-form-checkbox-group v-model="rowsFilter" :options="rowResultOptions" @change="loadRows(1)"/>
          </b-form-group>
        </b-col>
      </b-row>
      <b-alert v-if="rowsError" show variant="danger">{{ rowsError }} <b-btn size="sm" variant="link" @click="loadRows(rowsPage)">Retry</b-btn></b-alert>
      <b-table v-else :items="rows" :fields="rowFields" :busy="rowsLoading" small responsive show-empty empty-text="No rows.">
        <template #table-busy><p role="status">Loading rows...</p></template>
        <template #cell(source_record_id)="row">{{ readable(row.item.source_record_id) }}</template>
        <template #cell(result)="row">
          <b-badge :variant="rowResultVariant(row.item.result)">{{ row.item.result }}</b-badge>
        </template>
        <template #cell(reasons)="row">
          <div v-for="(reason, index) in row.item.reasons" :key="index" class="small">
            <b-badge :variant="reason.level === 'skip' ? 'danger' : reason.level === 'warning' ? 'warning' : 'light'">{{ reason.code }}</b-badge>
            {{ readable(reason.message) }}
          </div>
        </template>
        <template #cell(raw)="row">
          <b-btn size="sm" variant="link" @click="row.toggleDetails">{{ row.detailsShowing ? 'Hide' : 'Raw' }}</b-btn>
        </template>
        <template #row-details="row">
          <pre class="bg-light border rounded p-2 mb-0 overflow-auto license-registry-json"><code>{{ formatJson(row.item.raw) }}</code></pre>
        </template>
      </b-table>
      <b-pagination v-if="rowsMeta.total" :key="rowsRequest" :value="rowsPage" :total-rows="rowsMeta.total" :per-page="rowsMeta.per_page || 50"
                    align="center" @input="loadRows"/>
    </b-modal>
</section>
</template>

<script>
import moment from "moment-timezone";
import _ from "lodash";
import {API_ROUTES} from "../../api";
import {label, readable, evidence, dateTime, result, failure} from "./RegistryOperationSupport";
const ACTIVE_PHASES = ["validating", "waiting_for_worker", "continuing", "running"];
const POLL_INTERVAL_MS = 5000;
const PHASES = {
  validating: {label: "Validating", variant: "secondary"},
  validation_stalled: {label: "Validation stalled", variant: "warning"},
  rejected: {label: "Rejected", variant: "danger"},
  awaiting_apply: {label: "Awaiting apply", variant: "info"},
  waiting_for_worker: {label: "Waiting for worker", variant: "secondary"},
  continuing: {label: "Continuing", variant: "primary"},
  running: {label: "Running", variant: "primary"},
  stalled: {label: "Stalled", variant: "warning"},
  interrupted: {label: "Interrupted", variant: "warning"},
  completed: {label: "Completed", variant: "success"},
  completed_partial: {label: "Completed (partial)", variant: "info"}
};

function emptyUploadForm(timezone) {
  return {file: null, dataset_kind: null, declared_export_at: null, declared_export_timezone: timezone, scope_notes: null};
}


export default {
  name: "RegistryOperationImports",
  props: {canUpdate: Boolean, metadata: {type: Object, default: () => ({})}, beforeNavigate: {type: Function, default: async () => true}},
  data() { return {
    imports: [], importsMeta: {}, importsPage: 1, importsLoading: false, importsError: '', importsRequest: 0,
    uploadForm: emptyUploadForm(null), uploading: false, error: '', review: null, reviewNotice: null, reviewError: '', reviewRequest: 0,
    applying: false, retrying: false, applyAcknowledged: false, pollTimer: null, pollStopped: false, alive: true,
    rowsModalVisible: false, rowsBatch: null, rows: [], rowsMeta: {}, rowsPage: 1, rowsFilter: [], rowsLoading: false, rowsError: '', rowsRequest: 0,
    rowResultOptions: ["created", "updated", "unchanged", "skipped", "stale", "disputed", "contradicted", "pending"],
    rowFields: [{key: 'row_number', label: 'Row'}, {key: 'source_record_id', label: 'Supplier reference'}, {key: 'result', label: 'Result'}, {key: 'reasons', label: 'Reasons'}, {key: 'raw', label: ''}],
    importFields: [{key: 'file', label: 'File'}, {key: 'status', label: 'Status'}, {key: 'progress', label: 'Rows'}, {key: 'actions', label: ''}]
  }; },
  computed: {
    busy() { return this.uploading || this.applying || this.retrying; },
    dirty() { return !!(this.uploadForm.file || this.uploadForm.dataset_kind || this.uploadForm.declared_export_at || this.uploadForm.scope_notes || this.applyAcknowledged); },
    datasetKindOptions() { return [{value: null, text: 'Detect from headers'}, ...(this.metadata.dataset_kinds || []).map(value => ({value, text: label(value)}))]; },
    timezoneOptions() {
      // Lists only the identifiers that the server's timezone rule accepts. Moment's own list includes
      // aliases such as US/Mountain, which that rule rejects.
      return this.metadata.timezones || [];
    },
    uploadLimitBytes() { return (this.metadata.upload || {}).max_bytes; },
    uploadLimitDescription() { return this.uploadLimitBytes ? `Up to ${this.megabytes(this.uploadLimitBytes)} MB. Split national exports into state partitions.` : ''; },
    uploadFileError() { return this.uploadForm.file && this.uploadLimitBytes && this.uploadForm.file.size > this.uploadLimitBytes ? `File exceeds the ${this.megabytes(this.uploadLimitBytes)} MB limit.` : ''; },
    hasActiveWork() { return this.imports.some(batch => ACTIVE_PHASES.includes(batch.phase)) || this.review && ACTIVE_PHASES.includes(this.review.phase); }
  },
  watch: {dirty: {immediate: true, handler() { this.$emit('state', {dirty: this.dirty, busy: this.busy}); }}, busy() { this.$emit('state', {dirty: this.dirty, busy: this.busy}); }, metadata: {immediate: true, handler() { if (!this.uploadForm.declared_export_timezone) this.uploadForm.declared_export_timezone = this.defaultTimezone(); }}},
  mounted() { this.loadImports(1); },
  beforeDestroy() { this.alive = false; this.stopPolling(); this.importsRequest++; this.rowsRequest++; this.reviewRequest++; },
  methods: {
    labelize: label, readable, formatJson: evidence, formatDate: dateTime, formatUtc: dateTime,
    focusError() { this.$nextTick(() => { if (this.$refs.errorAlert) (this.$refs.errorAlert.$el || this.$refs.errorAlert).focus(); }); },
    // Uses the viewer's own zone when the server accepts that identifier, otherwise the server's zone.
    defaultTimezone() { const guess = moment.tz.guess(); return (this.metadata.timezones || []).includes(guess) ? guess : this.metadata.default_timezone || null; },
    async loadImports(page = 1, quiet = false) {
      if (!quiet) this.pollStopped = false;
      const request = ++this.importsRequest;
      this.importsLoading = true; this.importsError = '';
      try {
        const response = await this.$api.get(API_ROUTES.ADMIN_LICENSE_REGISTRY_IMPORTS, {params: {page}});
        if (request !== this.importsRequest) return;
        this.imports = result(response); this.importsMeta = response.data.meta || {}; this.importsPage = this.importsMeta.current_page || page;
      } catch (error) {
        if (request !== this.importsRequest) return;
        this.importsError = failure(error, 'Could not load import history.').message; this.pollStopped = true;
      } finally { if (request === this.importsRequest) { this.importsLoading = false; this.schedulePoll(); } }
    },
    stopPolling() { if (this.pollTimer) clearTimeout(this.pollTimer); this.pollTimer = null; },
    // Refreshes while this mounted tab has validating, queued or running batches. Only one
    // timer runs at a time. A failed refresh stops the timer until the next manual Refresh.
    schedulePoll() {
      this.stopPolling();
      if (!this.alive || !this.hasActiveWork || this.pollStopped || this.busy) return;
      this.pollTimer = setTimeout(async () => {
        this.pollTimer = null;
        if (this.review && ACTIVE_PHASES.includes(this.review.phase)) await this.loadReview(this.review.batch_id, true);
        if (!this.alive || this.pollStopped) return;
        await this.loadImports(this.importsPage, true);
      }, POLL_INTERVAL_MS);
    },
    async submitUpload() {
      if (!this.canUpdate || this.busy || !this.uploadForm.file || this.uploadFileError) return;
      const form = new FormData();
      form.append('file', this.uploadForm.file, this.uploadForm.file.name);
      if (this.uploadForm.declared_export_at) {
        // Sent as the wall time plus its zone, so the server records what the operator declared.
        form.append('declared_export_at', this.uploadForm.declared_export_at);
        form.append('declared_export_timezone', this.uploadForm.declared_export_timezone || '');
      }
      if (this.uploadForm.scope_notes) form.append('scope_notes', this.uploadForm.scope_notes);
      if (this.uploadForm.dataset_kind) form.append('dataset_kind', this.uploadForm.dataset_kind);
      this.uploading = true; this.error = ''; this.stopPolling();
      try {
        const batch = result(await this.$api.post(API_ROUTES.ADMIN_LICENSE_REGISTRY_IMPORTS, form, {headers: {'Content-Type': 'multipart/form-data'}}));
        this.uploadForm = emptyUploadForm(this.defaultTimezone());
        this.setReview(batch, this.uploadNotice(batch));
        this.$emit('changed', {import: batch});
      } catch (error) {
        this.error = failure(error, this.uploadErrorFallback(error.response)).message; this.focusError();
        const batch = _.get(error, 'response.data.result.data');
        if (batch && batch.batch_id) this.setReview(batch, {variant: 'danger', text: this.error});
      } finally { this.uploading = false; this.loadImports(1); }
    },
    setReview(batch, notice = null) { this.review = batch; this.reviewNotice = notice; this.applyAcknowledged = false; this.loadReview(batch.batch_id); },
    async openReview(batch, notice = null) { if (!this.busy && await this.beforeNavigate()) { this.uploadForm = emptyUploadForm(this.defaultTimezone()); this.setReview(batch, notice); } },
    async closeReview() { if (!this.busy && await this.beforeNavigate()) { this.uploadForm = emptyUploadForm(this.defaultTimezone()); this.review = null; this.reviewRequest++; this.applyAcknowledged = false; } },
    async loadReview(id, quiet = false) {
      const request = ++this.reviewRequest;
      this.reviewError = '';
      try {
        const batch = result(await this.$api.get(`${API_ROUTES.ADMIN_LICENSE_REGISTRY_IMPORTS}/${id}`));
        if (request !== this.reviewRequest || !this.review || this.review.batch_id !== id) return;
        const oldWarning = JSON.stringify(this.review.older_export);
        this.review = batch;
        if (oldWarning !== JSON.stringify(batch.older_export)) this.applyAcknowledged = false;
      } catch (error) {
        if (request !== this.reviewRequest) return;
        this.reviewError = failure(error, 'Could not refresh this batch.').message;
        if (quiet) { this.pollStopped = true; this.stopPolling(); }
      }
    },
    async applyImport(batch) {
      if (!this.canUpdate || this.busy || !(batch.available_actions || []).includes('apply') || batch.older_export && !this.applyAcknowledged) return;
      this.applying = true; this.error = ''; this.stopPolling();
      try {
        const view = result(await this.$api.post(`${API_ROUTES.ADMIN_LICENSE_REGISTRY_IMPORTS}/${batch.batch_id}/apply`, {acknowledge_older_export: this.applyAcknowledged}));
        this.applyAcknowledged = false;
        this.reviewNotice = view.dispatched === false && ACTIVE_PHASES.includes(view.phase) ? {variant: 'warning', text: 'Applied, but could not queue. Use Queue again to retry.'} : {variant: 'success', text: `${view.applied_now ? 'Applied' : 'Already applied'} with sequence ${view.accepted_sequence}.`};
        this.$emit('changed', {import: view});
      } catch (error) {
        const problem = failure(error, 'Could not apply this import.'); this.error = problem.message; this.focusError();
        // A later export was applied after this review loaded, so show it and ask again.
        if (problem.code === 'older_declared_export_date') this.applyAcknowledged = false;
      } finally { await this.loadReview(batch.batch_id); this.applying = false; this.loadImports(this.importsPage); }
    },
    async retryImport(batch, revalidate) {
      if (!this.canUpdate || this.busy || !(batch.available_actions || []).includes(revalidate ? 'revalidate' : 'retry')) return;
      if (!await this.beforeNavigate()) return;
      this.uploadForm = emptyUploadForm(this.defaultTimezone());
      this.retrying = true; this.error = ''; this.stopPolling();
      try {
        const view = result(await this.$api.post(`${API_ROUTES.ADMIN_LICENSE_REGISTRY_IMPORTS}/${batch.batch_id}/retry`, {revalidate}));
        this.setReview(view, {variant: view.dispatched === false ? 'warning' : 'info', text: revalidate ? 'Revalidated. Review the file before Apply.' : view.dispatched === false ? 'The batch could not be queued.' : 'Queued again. Waiting for a worker.'});
        this.$emit('changed', {import: view});
      } catch (error) { this.error = failure(error, 'Could not retry this import.').message; this.focusError(); }
      finally { this.retrying = false; this.loadImports(this.importsPage); }
    },
    openRows(batch) { this.rowsBatch = batch; this.rowsFilter = batch.counts.skipped ? ['skipped'] : []; this.rows = []; this.rowsMeta = {}; this.rowsModalVisible = true; this.loadRows(1); },
    async loadRows(page = 1) {
      if (!this.rowsBatch) return;
      const request = ++this.rowsRequest;
      this.rowsLoading = true; this.rowsError = '';
      try {
        const response = await this.$api.get(`${API_ROUTES.ADMIN_LICENSE_REGISTRY_IMPORTS}/${this.rowsBatch.batch_id}/rows`, {params: {result: this.rowsFilter, page}});
        if (request !== this.rowsRequest) return;
        this.rows = result(response); this.rowsMeta = response.data.meta || {}; this.rowsPage = this.rowsMeta.current_page || page;
      } catch (error) { if (request === this.rowsRequest) this.rowsError = failure(error, 'Could not load import rows.').message; }
      finally { if (request === this.rowsRequest) this.rowsLoading = false; }
    },
    uploadNotice(result) {
      if (!result.existing_batch) {
        return {variant: "info", text: "Validated and staged. Review the counts and warnings, then apply."};
      }
      if (result.duplicate_of) {
        return {variant: "success", text: `Already imported as batch #${result.batch_id}. Showing existing results.`};
      }
      return {variant: "info", text: `This exact file was already uploaded as batch #${result.batch_id}, which is ${this.phaseLabel(result.phase).toLowerCase()}.`};
    },
    uploadErrorFallback(response) {
      if (!response) {
        return "The upload did not reach the server. It may exceed a proxy or network limit.";
      }
      if (response.status === 413) {
        return "The upload is larger than the server accepts.";
      }
      return "Failed to upload the import.";
    },
    phaseLabel(phase) {
      return (PHASES[phase] || {label: this.labelize(phase)}).label;
    },
    phaseVariant(phase) {
      return (PHASES[phase] || {variant: "secondary"}).variant;
    },
    phaseDescription(batch) {
      const lastAttempt = _.last(batch.attempts || []);
      switch (batch.phase) {
        case "validating":
          return "Checking and staging the file.";
        case "validation_stalled":
          return "Validation stopped before it finished. Revalidate the stored file.";
        case "rejected":
          return "The file was not staged. Nothing was imported.";
        case "awaiting_apply":
          return "Ready for review. Apply to import this file.";
        case "waiting_for_worker":
          return `Queued ${this.since(batch.waiting_since)}. Waiting to import; files of this kind run one at a time.`;
        case "continuing":
          return `Waiting to resume ${this.since(batch.waiting_since)}. ${batch.processed_row_count} of ${batch.row_count} rows processed.`;
        case "running":
          return `Running · last heartbeat ${this.since(batch.heartbeat_at)}.`;
        case "stalled":
          return `No progress reported ${this.since(batch.heartbeat_at)}. Resume from the last completed rows.`;
        case "interrupted":
          return `Stopped after an error${lastAttempt && lastAttempt.error ? `: ${lastAttempt.error}` : ""}. Resume from the last completed rows.`;
        case "completed_partial":
          return "Completed. Some rows were skipped; see Rows.";
        case "completed":
          return "Completed.";
        default:
          return "";
      }
    },
    retryLabel(batch) {
      return ["waiting_for_worker", "continuing"].includes(batch.phase) ? "Queue again" : "Resume";
    },
    since(value) {
      return value ? moment(value).fromNow() : "at an unknown time";
    },
    countsSummary(batch) {
      const counts = batch.counts;
      const parts = [
        `${counts.created} created`,
        `${counts.updated} updated`,
        `${counts.unchanged} unchanged`,
        `${counts.skipped} skipped`
      ];
      ["stale", "disputed", "contradicted"].forEach((key) => {
        if (counts[key]) {
          parts.push(`${counts[key]} ${key}`);
        }
      });
      return parts.join(" · ");
    },
    observedStates(batch) {
      const states = batch.observed_states || {};
      const entries = Object.entries(states);
      return entries.length ? entries.map(([state, count]) => `${state} (${count})`).join(", ") : "N/A";
    },
    scopeNotes(batch) {
      if (batch.scope_notes) {
        return batch.scope_notes;
      }
      // Batches created before scope notes existed recorded their scope as filters.
      const filters = batch.requested_filters;
      if (!filters) {
        return null;
      }
      return filters.notes && Object.keys(filters).length === 1 ? filters.notes : Object.entries(filters).map(([key, value]) => `${key}: ${value}`).join(", ");
    },
    megabytes(bytes) {
      return Math.round((bytes / 1024 / 1024) * 10) / 10;
    },
    rowResultVariant(result) {
      return {created: "success", updated: "info", skipped: "danger", stale: "secondary", disputed: "warning", contradicted: "warning"}[result] || "light";
    }
  }
};
</script>
<style scoped>
.registry-import-evidence, .license-registry-json { white-space: pre-wrap; overflow-wrap: anywhere; max-height: 20rem; overflow: auto; font-size: .75rem; }
.registry-timezone { max-width: 14rem; }
.registry-notes { white-space: pre-wrap; }
</style>
